import React from "react";
import {
  Tree,
  TreeItem,
  TreeItemLayout,
  Caption1Strong,
  makeStyles,
  tokens,
  Body1,
} from "@fluentui/react-components";
import { FolderRegular, DocumentRegular } from "@fluentui/react-icons";
import { Category, Report } from "../../../types/reportTypes";
import { FontWeights } from "@fluentui/react";

const useStyles = makeStyles({
  root: {
    width: "230px",
    flex: "0 0 260px",
    height: "calc(100vh - 150px)",
    overflowY: "auto",
    backgroundColor: tokens.colorNeutralBackground4,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    boxSizing: "border-box",
    padding: "10px",
  },
  reportName: {
    color: "#424242",
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  header: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  tree: {
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
  categoryIcon: {
    color: tokens.colorNeutralForeground3,
  },
  reportIcon: {
    color: tokens.colorNeutralForeground3,
  },
  selectedLeaf: {
    backgroundColor: tokens.colorBrandBackground2,
    borderLeft: `3px solid ${tokens.colorBrandForeground1}`,
    color: "#000",
    fontWeight: tokens.fontWeightSemibold,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
  },
});

interface ReportCatalogSidebarProps {
  categories: Category[];
  selectedReport: Report | null;
  onSelectReport: (report: Report) => void;
}

// Persistent counterpart to ReportSelector's dropdown Tree — reuses the
// exact same branch/leaf detection ReportSelector.renderTreeItems already
// uses (a node is a branch if it has children, a leaf if it has a
// ReportCode), generalized over arbitrary depth: the real catalog nests
// categories 2-3 levels deep in places (e.g. all-clients -> commission-
// reports -> commission-due-reports -> CommissionDueReport). Fluent's Nav
// component only supports two levels of nesting, so Tree/TreeItem is used
// here instead — they manage their own expand/collapse state natively
// (uncontrolled), exactly like ReportSelector already relies on, with no
// depth limit. Folder/document icons and a shaded panel background give it
// the same nav-rail feel as the app's other sidebars even though it isn't
// built on the Nav component.
const ReportCatalogSidebar: React.FC<ReportCatalogSidebarProps> = ({
  categories,
  selectedReport,
  onSelectReport,
}) => {
  const styles = useStyles();

  function renderTreeItems(nodes: any[]) {
    return nodes.map((node: any) => {
      const hasChildren =
        Array.isArray(node.children) && node.children.length > 0;
      const isReport = !!node.ReportCode;
      const isSelected = selectedReport?.ReportCode === node.ReportCode;

      if (hasChildren) {
        const value = node.CategoryCode || node.Name;
        return (
          <TreeItem
            itemType="branch"
            key={value}
            value={value}
            className={styles.reportName}
          >
            <TreeItemLayout
            // iconBefore={<FolderRegular className={styles.categoryIcon} />}
            >
              {node.Name}
            </TreeItemLayout>
            <Tree>{renderTreeItems(node.children)}</Tree>
          </TreeItem>
        );
      }

      if (isReport) {
        return (
          <TreeItem
            itemType="leaf"
            key={node.ReportCode}
            value={node.ReportCode}
            onClick={() => onSelectReport(node)}
          >
            <TreeItemLayout
              // iconBefore={<DocumentRegular className={styles.reportIcon} />}
              className={isSelected ? styles.selectedLeaf : styles.reportName}
            >
              {node.Name}
            </TreeItemLayout>
          </TreeItem>
        );
      }

      return null;
    });
  }

  // Fluent's Tree is uncontrolled and starts fully collapsed by default —
  // fine for ReportSelector's transient dropdown, but this sidebar is
  // persistent precisely so the catalog doesn't have to be hidden behind a
  // click. Expand the top-level categories by default so the sidebar is
  // useful at a glance; deeper levels stay collapsed until the user drills
  // in, so a large catalog doesn't dump everything open at once.
  const defaultOpenItems = categories.map((c: any) => c.CategoryCode || c.Name);

  return (
    <div className={styles.root}>
      <Tree
        aria-label="Report categories"
        defaultOpenItems={defaultOpenItems}
        className={styles.tree}
      >
        {renderTreeItems(categories)}
      </Tree>
    </div>
  );
};

export default ReportCatalogSidebar;
